import { useState, useEffect } from 'react';
import { Crown, Gift, Star, Truck, ShieldCheck, Sparkles, Calendar, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = '';

const TIER_COLORS = {
  standard: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300', accent: '#999' },
  silver: { bg: 'bg-gradient-to-r from-gray-200 to-gray-100', text: 'text-gray-700', border: 'border-gray-400', accent: '#9CA3AF' },
  gold: { bg: 'bg-gradient-to-r from-[#F7E7CE] to-[#FFF8E7]', text: 'text-[#92710D]', border: 'border-[#D4AF37]', accent: '#D4AF37' },
  diamond: { bg: 'bg-gradient-to-r from-[#E8D5F5] to-[#F3E8FF]', text: 'text-purple-800', border: 'border-purple-400', accent: '#9333EA' },
};

const TIER_ICONS = { standard: Star, silver: Star, gold: Crown, diamond: Sparkles };

// ─── Exported Components ──────────────────────────────────────

export function VIPBadge({ level = 'standard', label = '', size = 'sm' }) {
  const colors = TIER_COLORS[level] || TIER_COLORS.standard;
  const Icon = TIER_ICONS[level] || Star;
  const sizes = { xs: 'text-[10px] px-1.5 py-0.5', sm: 'text-xs px-2 py-0.5', md: 'text-sm px-3 py-1' };
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full font-medium border', colors.bg, colors.text, colors.border, sizes[size])} data-testid="vip-badge">
      <Icon size={size === 'xs' ? 10 : size === 'sm' ? 12 : 14} />
      {label || level}
    </span>
  );
}

export function VIPHeaderBadge({ level = 'standard', label = '' }) {
  if (level === 'standard') return null;
  return <VIPBadge level={level} label={label} size="xs" />;
}

export function VIPDiscountBanner({ level = 'standard', label = '', discountPct = 0 }) {
  if (!discountPct || level === 'standard') return null;
  const colors = TIER_COLORS[level] || TIER_COLORS.standard;
  return (
    <div className={cn('flex items-center justify-between px-3 py-2 rounded border mb-3', colors.bg, colors.border)} data-testid="vip-discount-banner">
      <span className={cn('text-xs font-medium flex items-center gap-1.5', colors.text)}>
        <Crown size={14} /> Remise {label}
      </span>
      <span className={cn('text-sm font-bold', colors.text)}>-{discountPct}%</span>
    </div>
  );
}

// ─── Main VIP Profile Page ────────────────────────────────────

export default function VIPProfilePage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('status');
  const [birthday, setBirthday] = useState('');
  const [savingBirthday, setSavingBirthday] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/loyalty`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setData(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSaveBirthday = async () => {
    if (!birthday) return;
    setSavingBirthday(true);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/set-birthday`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ birthday })
      });
      if (res.ok) {
        toast.success(account.birthday ? 'Anniversaire mis a jour !' : 'Anniversaire enregistre !');
        setData(prev => ({ ...prev, account: { ...prev.account, birthday, birthday_locked: true } }));
        setBirthday('');
      } else {
        let detail = 'Erreur lors de la sauvegarde';
        try { const err = await res.json(); detail = err.detail || detail; } catch {}
        toast.error(detail);
      }
    } catch (e) {
      console.error('Birthday save error:', e);
      toast.error('Erreur reseau. Verifie ta connexion.');
    }
    finally { setSavingBirthday(false); }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center">
        <p className="text-[#6B6A68]">Connectez-vous pour voir votre statut VIP</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center">
        <div className="animate-spin w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
      </div>
    );
  }

  const account = data?.account || {};
  const tier = account.tier || 'standard';
  const tierInfo = data?.tier_info || {};
  const nextTier = data?.next_tier;
  const tiers = data?.tiers || {};
  const deliveredOrders = account.delivered_orders || 0;
  const colors = TIER_COLORS[tier];

  // Progress to next tier
  const currentMin = tiers[tier]?.min_orders || 0;
  const nextMin = nextTier?.min_orders || currentMin;
  const progress = nextTier ? Math.min(((deliveredOrders - currentMin) / (nextMin - currentMin)) * 100, 100) : 100;
  const ordersToNext = nextTier ? Math.max(nextMin - deliveredOrders, 0) : 0;

  const tabs = [
    { id: 'status', label: 'Mon statut' },
    { id: 'levels', label: 'Niveaux' },
    { id: 'perks', label: 'Avantages' },
  ];

  return (
    <div className="min-h-screen bg-[#FCFBF9] py-12" data-testid="vip-page">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className={cn('p-6 rounded-lg border-2 mb-8 text-center', colors.bg, colors.border)}>
          <VIPBadge level={tier} label={tierInfo.label} size="md" />
          <h1 className="text-2xl font-serif font-bold mt-3">{user.name}</h1>
          <p className="text-sm text-[#6B6A68] mt-1">{deliveredOrders} commande{deliveredOrders > 1 ? 's' : ''} livree{deliveredOrders > 1 ? 's' : ''}</p>
          {tierInfo.discount > 0 && (
            <p className={cn('text-lg font-bold mt-2', colors.text)}>-{tierInfo.discount}% sur toutes vos commandes</p>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#E0DFDD] mb-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex-1 py-3 text-sm font-medium text-center transition-colors',
                activeTab === tab.id ? 'border-b-2 border-[#1A1514] text-[#1A1514]' : 'text-[#6B6A68]'
              )}
              data-testid={`vip-tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'status' && (
          <div className="space-y-6">
            {/* Progress bar */}
            {nextTier && (
              <div className="bg-white p-5 border border-[#E0DFDD] rounded">
                <div className="flex justify-between text-sm mb-2">
                  <span className="font-medium">{tierInfo.label}</span>
                  <span className="text-[#6B6A68]">{nextTier.label}</span>
                </div>
                <div className="w-full h-3 bg-[#F0EFED] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${progress}%`, backgroundColor: colors.accent }}
                  />
                </div>
                <p className="text-xs text-[#6B6A68] mt-2 text-center">
                  Encore {ordersToNext} commande{ordersToNext > 1 ? 's' : ''} livree{ordersToNext > 1 ? 's' : ''} pour atteindre {nextTier.label} (-{nextTier.discount}%)
                </p>
              </div>
            )}
            {!nextTier && (
              <div className="bg-white p-5 border border-[#E0DFDD] rounded text-center">
                <Sparkles className="mx-auto mb-2 text-purple-500" size={24} />
                <p className="font-medium">Vous etes au niveau maximum !</p>
              </div>
            )}

            {/* Birthday */}
            <div className="bg-white p-5 border border-[#E0DFDD] rounded">
              <div className="flex items-center gap-2 mb-3">
                <Calendar size={16} className="text-[#D4AF37]" />
                <span className="text-sm font-medium">Date d'anniversaire</span>
              </div>
              {account.birthday && (
                <p className="text-xs text-[#6B6A68] mb-2">Actuelle : {account.birthday}</p>
              )}
              <div className="flex gap-2">
                <input
                  type="date"
                  value={birthday}
                  onChange={e => setBirthday(e.target.value)}
                  className="flex-1 border border-[#E0DFDD] px-3 py-2 text-sm focus:outline-none"
                  data-testid="birthday-input"
                />
                <button
                  onClick={handleSaveBirthday}
                  disabled={savingBirthday || !birthday}
                  className="px-4 py-2 bg-[#1A1514] text-white text-xs font-medium disabled:opacity-40"
                  data-testid="save-birthday-btn"
                >
                  {account.birthday ? 'Mettre a jour' : 'Enregistrer'}
                </button>
              </div>
              {tier === 'diamond' && (
                <p className="text-xs text-purple-600 mt-2">En tant que Diamond, vous recevrez un code -20% le jour de votre anniversaire !</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'levels' && (
          <div className="space-y-3">
            {['silver', 'gold', 'diamond'].map(t => {
              const info = tiers[t] || {};
              const tc = TIER_COLORS[t];
              const Icon = TIER_ICONS[t];
              const isCurrent = tier === t;
              return (
                <div key={t} className={cn('p-4 border rounded flex items-center gap-4', isCurrent ? `${tc.border} border-2 ${tc.bg}` : 'border-[#E0DFDD]')} data-testid={`level-${t}`}>
                  <div className={cn('w-12 h-12 rounded-full flex items-center justify-center', isCurrent ? tc.bg : 'bg-[#F5F4F2]')}>
                    <Icon size={20} style={{ color: tc.accent }} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{info.label}</span>
                      {isCurrent && <span className="text-[10px] bg-[#1A1514] text-white px-1.5 py-0.5 rounded">VOUS</span>}
                    </div>
                    <p className="text-xs text-[#6B6A68]">{info.min_orders}+ commandes livrees</p>
                  </div>
                  <span className={cn('text-lg font-bold', tc.text)}>-{info.discount}%</span>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'perks' && (
          <div className="space-y-3">
            {tier !== 'standard' && (
              <div className="bg-white p-4 border border-[#E0DFDD] rounded flex items-center gap-3">
                <Crown size={18} style={{ color: colors.accent }} />
                <span className="text-sm">Reduction permanente de <strong>-{tierInfo.discount}%</strong> sur toutes les commandes</span>
              </div>
            )}
            {(tier === 'gold' || tier === 'diamond') && (
              <>
                <div className="bg-white p-4 border border-[#E0DFDD] rounded flex items-center gap-3">
                  <Truck size={18} className="text-blue-500" />
                  <span className="text-sm">Livraison prioritaire</span>
                </div>
                <div className="bg-white p-4 border border-[#E0DFDD] rounded flex items-center gap-3">
                  <Gift size={18} className="text-pink-500" />
                  <span className="text-sm">Acces aux ventes privees</span>
                </div>
              </>
            )}
            {tier === 'diamond' && (
              <>
                <div className="bg-white p-4 border border-[#E0DFDD] rounded flex items-center gap-3">
                  <Calendar size={18} className="text-purple-500" />
                  <span className="text-sm">Cadeau anniversaire (-20% code unique valable 7 jours)</span>
                </div>
                <div className="bg-white p-4 border border-[#E0DFDD] rounded flex items-center gap-3">
                  <Sparkles size={18} className="text-purple-500" />
                  <span className="text-sm">Early access aux nouvelles collections</span>
                </div>
                <div className="bg-white p-4 border border-[#E0DFDD] rounded flex items-center gap-3">
                  <ShieldCheck size={18} className="text-purple-500" />
                  <span className="text-sm">Support client prioritaire</span>
                </div>
              </>
            )}
            {tier === 'standard' && (
              <div className="text-center py-8 text-[#6B6A68]">
                <p className="text-sm">Passez votre premiere commande pour debloquer le niveau Silver !</p>
                <ChevronRight className="mx-auto mt-2" size={16} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
